import { useCallback, useEffect, useState } from 'react';
import { MAX_PLAYER_NAME_LENGTH, type ScoreEntry } from '@photo-merge/shared';
import { fetchScores, submitScore } from '../lib/api';

const NAME_KEY = 'pm.playerName';

/**
 * One source of leaderboard state for a game, shared by the compact strip, the full
 * sheet, and the game-over panel — otherwise submitting a score in one place leaves
 * the others showing stale numbers.
 */
export function useScores(slug: string | null) {
  const [scores, setScores] = useState<ScoreEntry[] | null>(null);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  /** The entry this player just added, so it can be highlighted in the list. */
  const [mine, setMine] = useState<ScoreEntry | null>(null);

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    fetchScores(slug, controller.signal)
      .then(setScores)
      .catch(() => {
        if (!controller.signal.aborted) setScores([]);
      });
    return () => controller.abort();
  }, [slug]);

  const submit = useCallback(
    async (score: number) => {
      if (!slug) return;
      setSubmitting(true);
      setError(undefined);
      try {
        const trimmed = name.trim().slice(0, MAX_PLAYER_NAME_LENGTH);
        // Remembered so the next game doesn't ask again.
        if (trimmed) localStorage.setItem(NAME_KEY, trimmed);
        setScores(await submitScore(slug, trimmed, score));
        setMine({ name: trimmed || 'Anonymous', score, at: '' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save that score.');
      } finally {
        setSubmitting(false);
      }
    },
    [slug, name],
  );

  /** Clears the "just submitted" marker so a new round can submit again. */
  const reset = useCallback(() => setMine(null), []);

  return { scores, name, setName, submit, submitting, error, mine, reset };
}
